import { useEffect, useRef, useState } from "react";

import { buildAuthHeaders } from "../lib/http.ts";

const BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : (import.meta.env.VITE_API_BASE_URL as string);

/**
 * Loads a private meeting-media image (screenshots / infographics served from
 * GCS) through the authenticated API. Meeting media now requires the app bearer
 * + a server-side ownership (canRead) check, so a plain `<img src>` — which only
 * sends the IAP cookie — no longer works. This fetches the bytes with the
 * Firebase token, turns them into an object URL, and revokes it on cleanup.
 *
 * If `src` is already an inline `data:`/`blob:` URL or a fully-qualified http(s)
 * URL, it's used directly (no auth fetch).
 */
export function AuthImage({
  src,
  alt,
  className,
  style,
  onClick,
}: {
  src: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
}) {
  // What needs an authenticated fetch: our own API media, whether it arrives as
  // a root-relative path ("/meetings/…", prod) or an absolute URL under BASE
  // (dev). Inline data:/blob: and truly external http(s) URLs are used directly.
  const inline = src.startsWith("data:") || src.startsWith("blob:");
  const isApiAbsolute = BASE !== "" && src.startsWith(BASE);
  const isRelative = src.startsWith("/");
  const needsAuth = !inline && (isApiAbsolute || isRelative);
  const [objUrl, setObjUrl] = useState<string | null>(needsAuth ? null : src);
  const revokeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!needsAuth) {
      setObjUrl(src);
      return;
    }
    let cancelled = false;
    const url = isApiAbsolute ? src : `${BASE}${src}`;
    (async () => {
      try {
        let res = await fetch(url, { headers: await buildAuthHeaders() });
        if (res.status === 401) {
          // Token likely expired — refresh once and retry (mirrors api()).
          res = await fetch(url, { headers: await buildAuthHeaders(undefined, false, true) });
        }
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        const obj = URL.createObjectURL(blob);
        revokeRef.current = obj;
        setObjUrl(obj);
      } catch {
        /* leave placeholder */
      }
    })();
    return () => {
      cancelled = true;
      if (revokeRef.current) {
        URL.revokeObjectURL(revokeRef.current);
        revokeRef.current = null;
      }
    };
  }, [src, needsAuth, isApiAbsolute]);

  if (!objUrl) {
    // Lightweight placeholder while the authenticated bytes load.
    return <div className={className} style={{ ...style, background: "var(--surface-2, #eee)" }} />;
  }
  return <img src={objUrl} alt={alt} className={className} style={style} onClick={onClick} />;
}
